// The live photosphere: the sunspot record painted onto Sol's disc.
//
// `solRegions.ts` answers what is on the Sun at a Julian date; this module is
// the drawn side of that answer. It holds the region list in a small float
// texture, hands `sol.ts`'s fragment shader the uniforms to paint umbra,
// penumbra, faculae and granulation from it, and carries the artificial note
// the Layers panel reads. Nothing here fetches at boot. `load()`, called the
// first time a player opens Sol, fetches only the small model file (rotation,
// cycle model, source rights - a few KB); the 3.5+ MB record fetches only
// once the game clock lands somewhere inside the model's own recorded range,
// and at most once a session.

import * as THREE from 'three';

import {
  FACULA_MU_PEAK, GRANULE_DIAMETER_KM, GRANULE_LIFETIME_SECONDS, PLAGE_AREA_RATIO,
  SOL_MODEL_PATH, SOL_REGIONS_PATH, parseSolCatalogue, solPhotosphereAt,
  subEarthLongitudeDeg,
  type SolCatalogue, type SolCycleState, type SolRegion, type SolSource,
} from './solRegions.ts';
import { SOLAR_RADIUS_KM, subPixelFade } from './sol.ts';

/**
 * How many regions the shader can hold at once.
 *
 * The record's busiest day carries far fewer than this on the whole sphere -
 * the 1874-2016 mean is 5.1 a day and cycle 19's maximum peaks near 20 - so the
 * cap is headroom rather than a limit anyone reaches. Past it the LARGEST areas
 * win and `reading().capped` says how many were dropped, because a cap that
 * silently drops the great group of October 2003 would be worse than no cap.
 */
export const SOL_REGION_CAP = 64;

/**
 * How far the date may move before the region list is rebuilt, days.
 *
 * A spot moves about 14 degrees a day, so this is 0.28 degrees of drift - under
 * a tenth of the smallest drawn group's own radius. Between rebuilds the shader
 * carries each region forward at its OWN latitude's rate from `uSunDt`, so the
 * motion is continuous at every warp rate and this threshold only decides how
 * often the CPU re-reads the catalogue.
 */
export const SOL_REGION_STEP_DAYS = 0.02;

/** Umbral and penumbral intensity as a fraction of the local photosphere. */
const UMBRA_INTENSITY = 0.22;
const PENUMBRA_INTENSITY = 0.72;

/** Peak facular brightening over the local photosphere, at `FACULA_MU_PEAK`. */
const FACULA_PEAK_CONTRAST = 0.16;

/**
 * Smallest radius a group is drawn at, in screen pixels.
 *
 * A mean group is about 170 millionths of the hemisphere, which is 0.018 of the
 * solar radius: three and a half pixels on the 190-pixel disc a player sees on
 * opening Sol, and one pixel of umbra inside a penumbra that antialiases over
 * its own width. Drawn at its own size it is a speck nobody reads as a spot, so
 * a group under this is scaled about its centre until it reaches it - six
 * pixels of penumbra, which leaves the umbra wide enough to reach its own depth
 * rather than being averaged into the photosphere beside it. The record still
 * answers where each one is and how large it is; `live-sun-check.mjs` reads
 * that answer and never this.
 */
const SPOT_MIN_DRAWN_PX = 6.0;

/** And the cap on that floor, in solar radii, for a disc a few pixels wide. */
const SPOT_MAX_FLOOR_R = 0.05;

/** Peak-to-mean granulation contrast in white light. */
const GRANULE_CONTRAST = 0.11;

/**
 * Where granulation fades in, as the granule's OWN drawn angular diameter.
 *
 * A 1000 km granule is 1.4 arcsec at 1 AU, so at every distance a player
 * normally flies the pattern is orders under a pixel and drawing it there would
 * be noise rather than texture. Full contrast is at 0.05 deg a granule, which
 * is one pixel at the reference viewport - the same number the aureole's own
 * gate is written in - and the fade runs down the decade under it to nothing at
 * a fifth of a pixel. The Sun's disc is 70 degrees across where a granule is a
 * whole pixel and 14 where it is a fifth of one, so what a player sees at an
 * ordinary framing is a faint mottle that firms up as they close.
 */
const GRANULE_MIN_ANGULAR_DEG = 0.01;
const GRANULE_FULL_ANGULAR_DEG = 0.05;

/**
 * Where granulation fades OUT with the warp, in generations a real second.
 *
 * Full contrast up to one new field a second (600x), nothing from four (2400x):
 * 1 HR/S would otherwise swap the whole field six times a second and 1 DAY/S
 * draw a fresh one every frame, which reads as static rather than as a boil.
 */
const GRANULE_TURNOVER_FULL_PER_S = 1;
const GRANULE_TURNOVER_NONE_PER_S = 4;

/** The granule seed is wrapped to keep the hash's argument small. */
const GRANULE_SEED_WRAP = 8192;

const DEG = Math.PI / 180;

/** Radius ratio of the plage to the spot group it surrounds. */
const PLAGE_RADIUS_RATIO = Math.sqrt(PLAGE_AREA_RATIO);

export interface SolPhotosphereRef {
  /** One RGBA texel a region: sin(lat), Carrington lon, radius, umbra radius. */
  readonly regions: { value: THREE.DataTexture };
  readonly count: { value: number };
  /** Days from the instant the texels were written to the frame's own date. */
  readonly dt: { value: number };
  /** Sidereal a and b and the Carrington rate, radians a day. */
  readonly rot: { value: THREE.Vector3 };
  /** Carrington longitude minus body-fixed longitude, radians. */
  readonly lonOffset: { value: number };
  /** 1 while the live photosphere is what the disc draws. */
  readonly live: { value: number };
  /** Two generation seeds, the crossfade between them, and the sub-pixel fade. */
  readonly granule: { value: THREE.Vector4 };
  /** Granule cells across one solar radius. */
  readonly granuleScale: { value: number };
  /** 1 while `--inject sun-white-disc` holds the live layer off the map. */
  readonly bare: { value: number };
}

export interface SolPhotosphereReading {
  /** False until the model file lands; the disc still draws. */
  readonly loaded: boolean;
  /** False until the record file has been merged in; the disc draws modelled. */
  readonly recordLoaded: boolean;
  /** What went wrong, where a fetch or a parse failed. */
  readonly error: string | null;
  readonly jd: number;
  readonly measuredCount: number;
  readonly artificialCount: number;
  /** Regions the shader holds this frame, after the layer switch and the cap. */
  readonly drawnCount: number;
  /** How many the cap dropped; the largest areas are the ones kept. */
  readonly dropped: number;
  readonly totalAreaMillionths: number;
  readonly artificialAreaFraction: number;
  readonly cycle: SolCycleState | null;
  /** The player-facing note, '' when everything on the disc is measured. */
  readonly note: string;
  /** True while the live photosphere is drawn rather than the 304 A map. */
  readonly live: boolean;
  /** True while modelled regions are admitted. */
  readonly artificialOn: boolean;
  /**
   * The record's own provenance, out of the shipped artifact.
   *
   * Read from the catalogue rather than repeated in the registry row: the Sol
   * row already names the AIA 304 A map, and a second copy of a second source's
   * rights in a second file is the copy that goes stale.
   */
  readonly source: SolSource | null;
  /** Game seconds a real second the granulation was last faded for. */
  readonly granuleRate: number;
  /** The granule contrast that rate leaves, 1 held or slow and 0 at 1 HR/S up. */
  readonly granuleTurnoverFade: number;
}

export interface SolPhotosphereHandle {
  readonly ref: SolPhotosphereRef;
  /** Fetch and parse the model file, once. Safe to call from any frame. */
  load(): void;
  /**
   * Rebuild the region texels if the date has moved far enough, else carry.
   *
   * `subEarthLonBodyRad` is where the sub-EARTH point sits in the body-fixed
   * frame the disc's shader reconstructs. The record's longitudes are
   * Carrington's, and the two frames turn at the same rate about the same pole,
   * so the difference is the one constant offset between them - taken from the
   * caller's own ephemeris rather than assumed, and null where no Earth row is
   * in hand, which draws the regions on the body-fixed prime meridian instead.
   */
  update(jd: number, subEarthLonBodyRad: number | null): void;
  /** The granule fade, off Sol's own drawn angular diameter. */
  setDrawnAngularDiameterDeg(deg: number): void;
  /**
   * The granule fade, off how many game seconds the view draws a real second:
   * the clock's own rate, 0 while it is paused or a preview holds the date.
   * True when the fade moved and the disc needs a frame.
   */
  setClockRate(gameSecondsPerSecond: number): boolean;
  /** The LIVE SUN row: the live photosphere, or the 304 A map as it was. */
  setLive(on: boolean): void;
  /** The ARTIFICIAL FILL row: whether modelled regions are drawn at all. */
  setArtificialOn(on: boolean): void;
  /**
   * `--inject sun-frozen` holds the date the shader carries rotation from, so
   * two dates draw one frame; `--inject sun-no-spots` forces the region count
   * to zero; `--inject sun-eager-record` fetches the record file immediately
   * instead of waiting for the clock to enter its range, the control for the
   * lazy fetch itself; `--inject sun-white-disc` puts the live layer back on
   * the bare tinted disc instead of the 304 A map, which is the flat white
   * circle the frame leg exists to catch. Each is aimed at a different bar and
   * none touches the others. `rate` stands in for the clock's rate so a held
   * clock can be drawn as each warp rung draws it; null hands it back.
   * `strobe` ignores the rate, the granulation the rung fade replaced.
   */
  setDebug(d: {
    frozen?: boolean; noSpots?: boolean; eagerRecord?: boolean; whiteDisc?: boolean;
    rate?: number | null; strobe?: boolean;
  }): void;
  reading(): SolPhotosphereReading;
  /**
   * Every region the shader holds, carried to the frame's own date by the same
   * arithmetic the shader does, in the body-fixed frame it draws in.
   *
   * One source for the drawn position: a check reading this and a shader
   * reading the texels cannot disagree about where a spot is without the
   * texels themselves being wrong.
   */
  drawn(): readonly SolDrawnRegion[];
  dispose(): void;
}

/** One region as the shader has it this frame. */
export interface SolDrawnRegion {
  readonly id: string;
  readonly measured: boolean;
  readonly areaMillionths: number;
  readonly latDeg: number;
  /** Body-fixed longitude, radians: the Carrington one plus the frame offset. */
  readonly lonBodyRad: number;
  readonly radiusDeg: number;
  readonly umbraRadiusDeg: number;
}

/**
 * The GLSL the disc's fragment shader splices in.
 *
 * A spot is analytic - centre, radius and the region list - so zooming in
 * sharpens it rather than magnifying a texel, and the whole sphere's regions
 * ride in one texture that is rewritten a few times a game day at most.
 */
export const SOL_PHOTOSPHERE_PARS = /* glsl */ `
  uniform sampler2D uSunRegions;
  uniform int uSunCount;
  uniform float uSunDt;
  uniform vec3 uSunRot;
  uniform float uSunLonOffset;
  uniform float uSunLive;
  uniform vec4 uSunGranule;
  uniform float uSunGranuleScale;
  uniform float uSunBare;

  float solHash(vec3 c) {
    return fract(sin(dot(c, vec3(12.9898, 78.233, 37.719))) * 43758.5453);
  }

  /** Value noise on the body-fixed direction, one field a generation. */
  float solCells(vec3 x, float seed) {
    vec3 i = floor(x);
    vec3 f = fract(x);
    f = f * f * (3.0 - 2.0 * f);
    vec3 s = vec3(seed, seed * 0.7, seed * 1.3);
    float n000 = solHash(i + vec3(0.0, 0.0, 0.0) + s);
    float n100 = solHash(i + vec3(1.0, 0.0, 0.0) + s);
    float n010 = solHash(i + vec3(0.0, 1.0, 0.0) + s);
    float n110 = solHash(i + vec3(1.0, 1.0, 0.0) + s);
    float n001 = solHash(i + vec3(0.0, 0.0, 1.0) + s);
    float n101 = solHash(i + vec3(1.0, 0.0, 1.0) + s);
    float n011 = solHash(i + vec3(0.0, 1.0, 1.0) + s);
    float n111 = solHash(i + vec3(1.0, 1.0, 1.0) + s);
    return mix(
      mix(mix(n000, n100, f.x), mix(n010, n110, f.x), f.y),
      mix(mix(n001, n101, f.x), mix(n011, n111, f.x), f.y), f.z);
  }

  /**
   * Granulation as two generations crossfaded over a cell lifetime.
   *
   * The lifetime is GAME time, so the field crawls at a minute a second and
   * turns over once every ten minutes at real time; past a turnover a real
   * second, uSunGranule.w fades it out rather than let it strobe.
   */
  float solGranulation(vec3 p) {
    if (uSunGranule.w <= 0.0) return 1.0;
    vec3 g = p * uSunGranuleScale;
    float a = solCells(g, uSunGranule.x);
    float b = solCells(g, uSunGranule.y);
    float n = mix(a, b, uSunGranule.z);
    return 1.0 + ${GRANULE_CONTRAST.toFixed(3)} * (n * 2.0 - 1.0) * uSunGranule.w;
  }

  /** Facular contrast against mu, 0 at disc centre and 1 at the peak. */
  float solFaculaMu(float mu) {
    float m = clamp(mu, 0.0, 1.0);
    return (pow(m, 3.0) * pow(1.0 - m, 7.0)) / ${(
  Math.pow(FACULA_MU_PEAK, 3) * Math.pow(1 - FACULA_MU_PEAK, 7)).toExponential(6)};
  }

  /**
   * The region list painted onto one fragment.
   *
   * p is the fragment's body-fixed direction and aa its angular footprint, so
   * the spot edges antialias at every zoom. Each region's longitude is
   * carried from the instant the texels were written at its OWN latitude's
   * sidereal rate, which is what makes a spot near the equator cross the disc
   * faster than one at 60 degrees without the CPU touching the list.
   */
  vec2 solRegionField(vec3 p, float aa, float pxr) {
    float dark = 1.0;
    float plage = 0.0;
    // A group a pixel across cannot be drawn at its own size, the way a disc a
    // fifth of a pixel across cannot (SolOptions.minAngularDeg). Under the
    // floor the whole group is scaled about its own centre, umbra with
    // penumbra, so the shape and the place stay the record's and only the
    // drawn size is the floor's - capped so a Sun a few pixels wide is not
    // covered in blobs.
    float floorR = min(pxr * ${SPOT_MIN_DRAWN_PX.toFixed(1)}, ${SPOT_MAX_FLOOR_R.toFixed(2)});
    for (int i = 0; i < ${SOL_REGION_CAP}; i++) {
      if (i >= uSunCount) break;
      vec4 t = texture2D(uSunRegions,
        vec2((float(i) + 0.5) / ${SOL_REGION_CAP}.0, 0.5));
      float sl = t.x;
      float cl = sqrt(max(0.0, 1.0 - sl * sl));
      float lon = t.y + (uSunRot.x + uSunRot.y * sl * sl - uSunRot.z) * uSunDt
        + uSunLonOffset;
      vec3 q = vec3(cl * cos(lon), sl, -cl * sin(lon));
      // The chord rather than the arc: every drawn radius here is under 20
      // degrees, where the two agree to better than a part in two hundred.
      float ang = length(p - q);
      float grow = max(1.0, floorR / max(t.z, 1e-6));
      float rp = t.z * grow;
      float ru = t.w * grow;
      float pen = 1.0 - smoothstep(rp - aa, rp + aa, ang);
      float umb = 1.0 - smoothstep(ru - aa, ru + aa, ang);
      float f = mix(1.0, ${PENUMBRA_INTENSITY.toFixed(3)}, pen);
      f = mix(f, ${UMBRA_INTENSITY.toFixed(3)}, umb);
      dark = min(dark, f);
      float fr = rp * ${PLAGE_RADIUS_RATIO.toFixed(5)};
      plage = max(plage, (1.0 - smoothstep(fr * 0.5, fr, ang)) * (1.0 - pen));
    }
    return vec2(dark, plage);
  }

  /** The whole live layer over one fragment of the disc under it. */
  vec3 solPhotosphere(vec3 face, vec3 p, float mu, float aa, float pxr) {
    vec2 rf = solRegionField(p, aa, pxr);
    float bright = solGranulation(p) * rf.x
      * (1.0 + ${FACULA_PEAK_CONTRAST.toFixed(3)} * solFaculaMu(mu) * rf.y);
    return face * bright;
  }
`;

interface Drawn {
  readonly regions: readonly SolRegion[];
  readonly dropped: number;
}

/** The regions the shader holds: the layer switch, then the largest areas. */
function pickDrawn(all: readonly SolRegion[], artificialOn: boolean): Drawn {
  const admitted = artificialOn ? all.slice() : all.filter((r) => r.measured);
  if (admitted.length <= SOL_REGION_CAP) return { regions: admitted, dropped: 0 };
  admitted.sort((a, b) => b.areaMillionths - a.areaMillionths);
  return { regions: admitted.slice(0, SOL_REGION_CAP), dropped: admitted.length - SOL_REGION_CAP };
}

export function createSolPhotosphere(): SolPhotosphereHandle {
  const data = new Float32Array(SOL_REGION_CAP * 4);
  const regions = new THREE.DataTexture(
    data, SOL_REGION_CAP, 1, THREE.RGBAFormat, THREE.FloatType);
  regions.name = 'sol-regions';
  regions.magFilter = THREE.NearestFilter;
  regions.minFilter = THREE.NearestFilter;
  regions.generateMipmaps = false;
  regions.needsUpdate = true;

  const ref: SolPhotosphereRef = {
    regions: { value: regions },
    count: { value: 0 },
    dt: { value: 0 },
    rot: { value: new THREE.Vector3() },
    lonOffset: { value: 0 },
    live: { value: 0 },
    granule: { value: new THREE.Vector4(0, 1, 0, 0) },
    granuleScale: { value: SOLAR_RADIUS_KM / GRANULE_DIAMETER_KM },
    bare: { value: 0 },
  };

  let cat: SolCatalogue | null = null;
  let modelRaw: unknown = null;
  let asked = false;
  let recordAsked = false;
  let error: string | null = null;
  let disposed = false;
  let live = true;
  let artificialOn = true;
  let frozen = false;
  let noSpots = false;
  let eagerRecordDebug = false;

  let builtJd = Number.NaN;
  let frameJd = Number.NaN;
  let measuredCount = 0;
  let artificialCount = 0;
  let drawnCount = 0;
  let dropped = 0;
  let totalAreaMillionths = 0;
  let artificialAreaFraction = 0;
  let cycle: SolCycleState | null = null;
  let note = '';
  let granuleFade = 0;
  let clockRate = 0;
  let rateDebug: number | null = null;
  let strobe = false;
  let turnoverFade = 1;
  let drawnList: readonly SolRegion[] = [];

  function syncCount(): void {
    ref.count.value = noSpots ? 0 : drawnCount;
  }

  function applyTurnover(): boolean {
    const perS = Math.abs(rateDebug ?? clockRate) / GRANULE_LIFETIME_SECONDS;
    const next = strobe ? 1 : 1 - subPixelFade(perS, GRANULE_TURNOVER_FULL_PER_S, GRANULE_TURNOVER_NONE_PER_S);
    if (next === turnoverFade) return false;
    turnoverFade = next;
    ref.granule.value.w = granuleFade * turnoverFade;
    return true;
  }

  function build(jd: number): void {
    if (cat === null) return;
    const photo = solPhotosphereAt(cat, jd);
    const drawn = pickDrawn(photo.regions, artificialOn);
    for (let i = 0; i < drawn.regions.length; i += 1) {
      const r = drawn.regions[i]!;
      data[i * 4] = Math.sin(r.latDeg * DEG);
      data[i * 4 + 1] = r.lonDeg * DEG;
      data[i * 4 + 2] = r.radiusDeg * DEG;
      data[i * 4 + 3] = r.umbraRadiusDeg * DEG;
    }
    regions.needsUpdate = true;
    builtJd = jd;
    drawnList = drawn.regions;
    drawnCount = drawn.regions.length;
    dropped = drawn.dropped;
    measuredCount = photo.measuredCount;
    artificialCount = photo.artificialCount;
    totalAreaMillionths = photo.totalAreaMillionths;
    artificialAreaFraction = photo.artificialAreaFraction;
    cycle = photo.cycle;
    note = photo.note;
    const rot = cat.rotation;
    ref.rot.value.set(rot.a * DEG, rot.b * DEG, rot.carringtonSidereal * DEG);
    syncCount();
  }

  /**
   * Fetch the record file, once, when the date warrants it.
   *
   * `eagerRecordDebug` (`--inject sun-eager-record`) is the control for this
   * gate: it drops the date check so the arm can prove the ungated fetch is
   * what this function refuses to do by default.
   */
  function maybeFetchRecord(jd: number): void {
    if (cat === null || recordAsked || cat.recordLoaded) return;
    if (!eagerRecordDebug
      && (!Number.isFinite(jd) || jd < cat.recordStartJd || jd > cat.recordEndJd)) return;
    recordAsked = true;
    void fetch(`/${SOL_REGIONS_PATH}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`${SOL_REGIONS_PATH} answered ${res.status}`);
        return res.json();
      })
      .then((recordJson) => {
        // A late resolve after the layer went off or the handle disposed
        // builds nothing: the caller no longer wants this catalogue drawn.
        if (disposed || !live || cat === null) return;
        cat = parseSolCatalogue(modelRaw, recordJson);
        if (Number.isFinite(frameJd)) build(frameJd);
      })
      .catch((err: unknown) => {
        error = err instanceof Error ? err.message : String(err);
        console.warn('[atlas] the sunspot record did not load; the Sun draws modelled regions:', err);
      });
  }

  return {
    ref,
    load(): void {
      if (asked || disposed) return;
      asked = true;
      void fetch(`/${SOL_MODEL_PATH}`)
        .then(async (res) => {
          if (!res.ok) throw new Error(`${SOL_MODEL_PATH} answered ${res.status}`);
          return res.json();
        })
        .then((json) => {
          if (disposed) return;
          modelRaw = json;
          cat = parseSolCatalogue(json);
          if (Number.isFinite(frameJd)) build(frameJd);
          maybeFetchRecord(frameJd);
        })
        .catch((err: unknown) => {
          // Never into the frame loop: the disc keeps its limb darkening and
          // its granulation, and the note says the record is not in hand.
          error = err instanceof Error ? err.message : String(err);
          console.warn('[atlas] the sunspot model did not load; the Sun draws its bare limb:', err);
        });
    },
    update(jd: number, subEarthLonBodyRad: number | null): void {
      frameJd = jd;
      // Nothing below runs while the model has not landed or the layer is
      // off: the shader draws the 304 A map or the bare disc either way, so
      // there is nothing here for a frame to spend on.
      if (cat === null || !live) return;
      maybeFetchRecord(jd);
      if (subEarthLonBodyRad !== null) {
        ref.lonOffset.value = subEarthLongitudeDeg(cat.rotation, jd) * DEG - subEarthLonBodyRad;
      }
      if (!frozen && (!Number.isFinite(builtJd) || Math.abs(jd - builtJd) > SOL_REGION_STEP_DAYS)) {
        build(jd);
      }
      ref.dt.value = frozen ? 0 : jd - builtJd;
      // `granulation()`'s arithmetic inline: this runs every frame, and its
      // answer is an object.
      const gt = ((frozen ? builtJd : jd) * 86400) / GRANULE_LIFETIME_SECONDS;
      const gen = Math.floor(gt);
      const seed = ((gen % GRANULE_SEED_WRAP) + GRANULE_SEED_WRAP) % GRANULE_SEED_WRAP;
      ref.granule.value.set(seed, (seed + 1) % GRANULE_SEED_WRAP, gt - gen, granuleFade * turnoverFade);
    },
    setDrawnAngularDiameterDeg(deg: number): void {
      const granuleDeg = deg * (GRANULE_DIAMETER_KM / (2 * SOLAR_RADIUS_KM));
      granuleFade = subPixelFade(granuleDeg, GRANULE_MIN_ANGULAR_DEG, GRANULE_FULL_ANGULAR_DEG);
      ref.granule.value.w = granuleFade * turnoverFade;
    },
    setClockRate(gameSecondsPerSecond: number): boolean {
      clockRate = gameSecondsPerSecond;
      return applyTurnover();
    },
    setLive(on: boolean): void {
      live = on;
      ref.live.value = on ? 1 : 0;
    },
    setArtificialOn(on: boolean): void {
      if (artificialOn === on) return;
      artificialOn = on;
      if (cat !== null && Number.isFinite(frameJd)) build(frameJd);
    },
    setDebug(d: {
      frozen?: boolean; noSpots?: boolean; eagerRecord?: boolean; whiteDisc?: boolean;
      rate?: number | null; strobe?: boolean;
    }): void {
      if (d.whiteDisc !== undefined) ref.bare.value = d.whiteDisc ? 1 : 0;
      if (d.rate !== undefined) rateDebug = d.rate;
      if (d.strobe !== undefined) strobe = d.strobe;
      if (d.rate !== undefined || d.strobe !== undefined) applyTurnover();
      if (d.frozen !== undefined) frozen = d.frozen;
      if (d.noSpots !== undefined) { noSpots = d.noSpots; syncCount(); }
      if (d.eagerRecord !== undefined) {
        eagerRecordDebug = d.eagerRecord;
        if (eagerRecordDebug) maybeFetchRecord(frameJd);
      }
    },
    reading(): SolPhotosphereReading {
      return {
        loaded: cat !== null,
        recordLoaded: cat?.recordLoaded ?? false,
        error,
        jd: frameJd,
        measuredCount,
        artificialCount,
        drawnCount: ref.count.value,
        dropped,
        totalAreaMillionths,
        artificialAreaFraction,
        cycle,
        note: cat === null
          ? (error === null
            ? 'The sunspot record is still loading; the disc is its limb darkening alone.'
            : 'The sunspot record did not load, so no region is drawn on this disc.')
          : note,
        live,
        artificialOn,
        source: cat?.source ?? null,
        granuleRate: rateDebug ?? clockRate,
        granuleTurnoverFade: turnoverFade,
      };
    },
    drawn(): readonly SolDrawnRegion[] {
      if (noSpots || cat === null) return [];
      const rot = ref.rot.value;
      const dt = ref.dt.value;
      return drawnList.map((r) => {
        const sl = Math.sin(r.latDeg * DEG);
        const rate = rot.x + rot.y * sl * sl - rot.z;
        return {
          id: r.id,
          measured: r.measured,
          areaMillionths: r.areaMillionths,
          latDeg: r.latDeg,
          lonBodyRad: r.lonDeg * DEG + rate * dt + ref.lonOffset.value,
          radiusDeg: r.radiusDeg,
          umbraRadiusDeg: r.umbraRadiusDeg,
        };
      });
    },
    dispose(): void {
      if (disposed) return;
      disposed = true;
      regions.dispose();
    },
  };
}
